<template>
  <div>
    <ToastNotifications />
    <NavBar v-if="isCoreDataSuccess && !showAlternateHeader" />
    <RateConnectStickyBanner v-else-if="showAlternateHeader" />
    <StudioPlusReactivationModal
      v-if="isAdminUser"
      :expired="isExpiredSubscription"
      :cancelled="isCancelledSubscription"
    />
    <ProToPlusReactivationModal v-if="isAdminUser" />
    <div
      class="tw:min-h-screen-minus-footer tw:flex tw:justify-center tw:items-center tw:w-full tw:sm:pt-nav-h tw:pt-[100px]"
    >
      <div v-if="isCoreDataError">
        <ErrorModal
          header-key="dashboard.loadFail.header.launch"
          retry-key="dashboard.loadFail.retryButton"
          @retry="handleRetry"
        />
      </div>
      <div v-else-if="isCoreDataLoading" class="tw:flex tw:justify-center tw:items-center">
        <LaunchpadLoader />
      </div>
      <div v-else-if="isCoreDataSuccess" class="tw:w-full">
        <MainNotification />
        <div v-if="route.meta.isFullWidth">
          <slot />
        </div>
        <ContainerLayout v-else>
          <slot />
        </ContainerLayout>
      </div>
    </div>
    <ThePageFooter />
    <ChatWidget v-if="isChatbotVisible" />
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { useStore } from 'vuex'
import { useRoute } from 'vue-router'

import ThePageFooter from '@/components/page/ThePageFooter.vue'
import ContainerLayout from '@/layouts/ContainerLayout.vue'
import NavBar from '@/components/dashboard/nav/NavBar.vue'
import LaunchpadLoader from '@/components/dashboard/common/LaunchpadLoader.vue'
import ToastNotifications from '@/components/notifications/ToastNotifications.vue'
import MainNotification from '@/components/productNotifications/MainNotification.vue'
import StudioPlusReactivationModal from '@/components/billing/premiumProducts/reactivation/StudioPlusReactivationModalContainer.vue'
import ProToPlusReactivationModal from '@/components/billing/premiumProducts/reactivation/ProToPlusReactivationModalContainer.vue'
import ErrorModal from '@/components/dashboard/ErrorModal.vue'
import RateConnectStickyBanner from '@/components/rateConnect/onboarding/RateConnectStickyBanner.vue'
import ChatWidget from '@/components/chatbot/ChatWidget.vue'

import {
  useUserDetails,
  useMultipleOrganisationAssignmentsQueries,
  useOrganisationAssignments,
  useAccommodationsByOrgId,
  useMultipleAccommodationsByOrgIdQueries,
} from '@/layouts/queries'
import { useGetUserCurrency } from '@/components/locale/queries'
import { useStudioPlusCampaign } from '@/components/payment/queries'
import { useRateConnectOnboardingData } from '@/components/rateConnect/onboarding/useRateConnectOnboardingData'
import { useStudioPlusReactivation } from '@/components/billing/premiumProducts/reactivation/useStudioPlusReactivation'
import { useSessionValues } from '@/composables/useSessionValues'

const store = useStore()
const route = useRoute()
const isGuest = computed(() => false)

const isChatbotVisible = import.meta.env.VITE_CHATBOT_VISIBLE === 'true'

const { showAlternateHeader } = useRateConnectOnboardingData()
const { reactivationAccommodationId } = useStudioPlusReactivation()
const { userId, selectedOrganisationId, userRole } = useSessionValues()

// Loading currency and language
const { isOrganisationsCurrencyLoading } = useGetUserCurrency({ isGuest })

// Loading user data
const { isUserDetailsFetching, isUserDetailsSuccess, isUserDetailsError } = useUserDetails(userId)

// Loading selected organisation assignments
const { isOrganisationAssignmentsFetching, isOrganisationAssignmentsSuccess, isOrganisationAssignmentsError } =
  useOrganisationAssignments(selectedOrganisationId)

// Loading accommodations for selected organisation.
const { isAccommodationsByOrgIdSuccess, isAccommodationsByOrgIdError, isAccommodationsByOrgIdInitialLoading } =
  useAccommodationsByOrgId(selectedOrganisationId)

// Preloading non-selected organisation assignments and accommodations.
const organisations = computed(() => store.state.session.organisations)
useMultipleOrganisationAssignmentsQueries(organisations)
useMultipleAccommodationsByOrgIdQueries(organisations)

// Loading indicators
const isSessionLoading = computed(() => store.state.session.isLoading)
const isSessionSuccess = computed(() => store.state.session.isSuccess)
const isSessionError = computed(() => store.state.session.isError)

const isCoreDataLoading = computed(
  () =>
    isSessionLoading.value ||
    isUserDetailsFetching.value ||
    isOrganisationAssignmentsFetching.value ||
    isAccommodationsByOrgIdInitialLoading.value ||
    isOrganisationsCurrencyLoading.value
)

const isCoreDataSuccess = computed(
  () =>
    !isCoreDataLoading.value &&
    isSessionSuccess.value &&
    isUserDetailsSuccess.value &&
    isOrganisationAssignmentsSuccess.value &&
    isAccommodationsByOrgIdSuccess.value
)

const isCoreDataError = computed(
  () =>
    isSessionError.value ||
    isUserDetailsError.value ||
    isOrganisationAssignmentsError.value ||
    isAccommodationsByOrgIdError.value
)

// Other functionality
const handleRetry = () => {
  store.dispatch('session/restoreStudioSession')
}

// Data for subscription reactivation
const isAdminUser = computed(() => userRole.value === 'admin')

const { isExpiredSubscription, isCancelledSubscription } = useStudioPlusCampaign(reactivationAccommodationId)
</script>
